function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable max-statements */
import React from 'react';
import BN from 'bn.js';
import { AppError, EipError } from 'src/redux/types';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { checkoutItem, fetchHeroDetail, fetchTransactionConfirm, invalidateCheckoutItem } from 'src/redux/actions';
import { updateWalletBalance } from '@profile/src/redux/remoteActions';
import PageContent from 'src/Components/PageContent';
import Spinner from 'src/Components/Spinner';
import EmptyList from 'src/Components/EmptyList';
import CheckoutError from './CheckoutError';
import styles from './styles.module.scss';
import icClose from 'assets/svg/ic_close.svg';
import { formatCurrency } from '@wolffun/system-currency';
import { logBuyHeroEvent, logCoinChangeMarket } from 'src/utils/analytic';
import { formatErrorMessage } from '../../utils';
import Modal from 'src/Components/Modal';
import { getOwnerOfItem } from 'src/utils/ethereum';
import Button from '../../Components/Button';
import { getPreferredWalletProviderName } from 'src/utils/session';
import { HeroStatus } from 'src/utils/types';
import { THETA_MARKETPLACE_WALLET_ADDRESS } from 'src/constants';
import useCheckAllowance from 'src/hooks/useCheckAllowance';
import { useTestId } from '@wolffun/testable-id';
const TIME_LIMIT = 60;

const TransactionConfirmModal = ({
  hero,
  onClose,
  onCheckoutCompleted
}) => {
  const {
    generateTestIdProps
  } = useTestId();
  const dispatch = useTypedDispatch();
  const invalidated = useTypedSelector(state => state.transactionConfirm.invalidated);
  const loading = useTypedSelector(state => state.transactionConfirm.loading);
  const error = useTypedSelector(state => state.transactionConfirm.error);
  const price = useTypedSelector(state => {
    var _state$itemDetail$her;

    return (_state$itemDetail$her = state.itemDetail.heroDetail) === null || _state$itemDetail$her === void 0 ? void 0 : _state$itemDetail$her.hero.systemPrice;
  });
  const systemPrice = useTypedSelector(state => {
    var _state$itemDetail$her2;

    return (_state$itemDetail$her2 = state.itemDetail.heroDetail) === null || _state$itemDetail$her2 === void 0 ? void 0 : _state$itemDetail$her2.hero.systemPrice;
  });
  const thgBalance = useTypedSelector(state => state.connectWallet.thgBalance);
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const userId = useTypedSelector(state => {
    var _state$userProfile$us;

    return (_state$userProfile$us = state.userProfile.user) === null || _state$userProfile$us === void 0 ? void 0 : _state$userProfile$us.id;
  });
  const checkoutLoading = useTypedSelector(state => state.itemCheckout.loading);
  const [showWaiting, setShowWaiting] = React.useState(false);
  const [checkoutError, setCheckoutError] = React.useState(AppError.NO_ERROR);
  const [countDown, setCountDown] = React.useState(TIME_LIMIT);
  const [blockchainHeroOwner, setBlockchainHeroOwner] = React.useState('');
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
  const [errorTitle, setErrorTitle] = React.useState('');
  const [errorDescription, setErrorDescription] = React.useState('');
  const isNotDetectedOwner = blockchainHeroOwner === '';
  const isOwnerChanged = isNotDetectedOwner ? false : blockchainHeroOwner.toLowerCase() !== THETA_MARKETPLACE_WALLET_ADDRESS.toLowerCase();
  const {
    approveUseBalance,
    isEnoughAllowance,
    isProcessing
  } = useCheckAllowance({
    contractAddress: THETA_MARKETPLACE_WALLET_ADDRESS,
    thresholdPrice: systemPrice
  });
  React.useEffect(() => {
    const getOwner = async () => {
      const {
        ownerAddress
      } = await getOwnerOfItem(hero.hero.tokenId);
      setBlockchainHeroOwner(ownerAddress);
    };

    getOwner();
  }, [hero]);
  React.useEffect(() => {
    const priceBN = price ? new BN(price.value) : undefined;
    const thetaBalanceBN = thcBalance ? new BN(thcBalance.value) : undefined;
    const isEnoughBalance = thetaBalanceBN && priceBN && thetaBalanceBN.gte(priceBN);
    const isItemAvailable = hero.hero.status === HeroStatus.ON_MARKET;

    if (!isItemAvailable) {
      setIsButtonDisabled(true);
      setErrorTitle('Item unavailable');
      setErrorDescription('The item is no longer listed on marketplace.');
      return;
    }

    if (!isEnoughBalance) {
      setIsButtonDisabled(true);
      setErrorTitle('');
      setErrorDescription('Your balance is not enough funds. Please deposit more and submit again');
      return;
    }

    if (checkoutError !== AppError.NO_ERROR) {
      setErrorTitle('Opps! Something went wrong');
      setErrorDescription(formatErrorMessage(checkoutError));
      return;
    }

    setIsButtonDisabled(false);
    setErrorTitle('');
    setErrorDescription('');
  }, [blockchainHeroOwner, checkoutError, hero.hero.userAddress, price, thcBalance]);
  React.useEffect(() => {
    if (!isButtonDisabled) {
      const timeout = setTimeout(() => {
        if (countDown === 0) {
          dispatch(fetchHeroDetail(hero.hero.id));
          fetchData();
          setCountDown(TIME_LIMIT);
        } else {
          setCountDown(pre => pre - 1);
        }
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [countDown, dispatch, hero.hero.id]);
  React.useEffect(() => {
    if (checkoutLoading) {
      const timeout = setTimeout(() => setShowWaiting(true), 10 * 1000);
      return () => clearTimeout(timeout);
    } else {
      setShowWaiting(false);
      return undefined;
    }
  }, [checkoutLoading]);
  React.useEffect(() => {
    fetchData();
  }, []);
  const {
    current: handleBeforeUnloadEvent
  } = React.useRef(e => {
    e.preventDefault();
    e.returnValue = 'Checkout is still in process. Are you sure you want to leave this page?';
  });
  React.useEffect(() => {
    if (checkoutLoading) {
      window.addEventListener('beforeunload', handleBeforeUnloadEvent);
    }

    return () => window.removeEventListener('beforeunload', handleBeforeUnloadEvent);
  }, [checkoutLoading]);

  const fetchData = () => {
    dispatch(updateWalletBalance());
    dispatch(invalidateCheckoutItem());
    dispatch(fetchTransactionConfirm());
  };

  const onCheckoutButtonPressed = async () => {
    if (systemPrice) {
      const {
        blockchainId
      } = hero.hero;
      const {
        error,
        data
      } = await dispatch(checkoutItem({
        systemPrice,
        blockchainId
      }));

      if (error) {
        setCheckoutError(error);
        return;
      }

      if (data !== null && data !== void 0 && data.id) {
        onCheckoutCompleted(hero.hero.nftId, hero.heroType.name, systemPrice);

        if (thcBalance && thgBalance && userId) {
          const {
            battleCapTHC,
            totalBattleCapTHC,
            level,
            rarity,
            tokenId,
            heroTypeId,
            skinRarity,
            trophy
          } = hero.hero;
          logBuyHeroEvent({
            THC: thcBalance,
            THG: thgBalance,
            battleCapTHC: battleCapTHC,
            limitTHCBattleCap: totalBattleCapTHC,
            heroLevel: level,
            heroRarity: rarity,
            heroToken: tokenId,
            heroTypeId: heroTypeId,
            price: systemPrice,
            skinRarity: skinRarity,
            trophyLevel: trophy,
            transactionId: data.id,
            userId
          });
          logCoinChangeMarket({
            THC: thcBalance,
            THG: thgBalance,
            price: systemPrice,
            transactionId: data.id,
            userId,
            source: 'BuyHero'
          });
        }
      }
    }
  };

  const renderError = () => /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.placeholder
  }, /*#__PURE__*/React.createElement(EmptyList, {
    title: "Something went wrong, please try again"
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, /*#__PURE__*/React.createElement(Button, {
    title: "Cancel",
    type: "secondary"
  }), /*#__PURE__*/React.createElement(Button, {
    title: "Checkout",
    disabled: true
  })));

  const renderLoading = () => /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.placeholer
  }, /*#__PURE__*/React.createElement(Spinner, null)), /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, /*#__PURE__*/React.createElement(Button, {
    title: "Cancel",
    type: "secondary"
  }), /*#__PURE__*/React.createElement(Button, {
    title: "Checkout",
    disabled: true
  })));

  const renderContent = () => /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subtitle
  }, `You are about to purchase #${hero.hero.tokenId} ${hero.skin.name} ${hero.heroType.name}`), /*#__PURE__*/React.createElement("div", {
    className: styles.priceBreakdownRow
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Your balance"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.price
  }, generateTestIdProps('user-balance')), thcBalance && formatCurrency(thcBalance, {
    withCurrency: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.priceRow
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Price"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.price
  }, generateTestIdProps('hero-price')), price && formatCurrency(price, {
    withCurrency: true
  }))), !isButtonDisabled && /*#__PURE__*/React.createElement("span", {
    className: styles.resetPrice
  }, "Price will be updated in ", /*#__PURE__*/React.createElement("span", null, countDown, "s")), !!errorTitle || !!errorDescription ? /*#__PURE__*/React.createElement(CheckoutError, {
    className: styles.error,
    title: errorTitle,
    description: errorDescription
  }) : isOwnerChanged ? /*#__PURE__*/React.createElement(CheckoutError, {
    className: styles.error,
    warning: true,
    title: "Item unavailable",
    description: "The item is no longer listed on marketplace."
  }) : null, showWaiting ? /*#__PURE__*/React.createElement(CheckoutError, {
    className: styles.error,
    warning: true,
    title: "It may take a while...",
    description: `We are still waiting for transaction to be processed. Please be patient. You might need to check your ${getPreferredWalletProviderName() || ''} wallet.`
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, /*#__PURE__*/React.createElement(Button, {
    title: "Cancel",
    onClick: onClose,
    disabled: checkoutLoading,
    type: "secondary"
  }), isEnoughAllowance ? /*#__PURE__*/React.createElement(Button, {
    title: "Checkout",
    onClick: onCheckoutButtonPressed,
    disabled: isButtonDisabled,
    loading: checkoutLoading
  }) : /*#__PURE__*/React.createElement(Button, {
    title: "Approve",
    onClick: async () => {
      const {
        error
      } = await approveUseBalance();

      if (error !== EipError.NO_ERROR) {
        setCheckoutError(error);
      }
    },
    disabled: isButtonDisabled,
    loading: isProcessing
  })));

  return /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Checkout"), /*#__PURE__*/React.createElement("button", {
    className: styles.close,
    disabled: checkoutLoading,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("img", {
    src: icClose
  }))), /*#__PURE__*/React.createElement(PageContent, {
    invalidated: invalidated,
    loading: loading,
    error: error !== AppError.NO_ERROR,
    renderContent: renderContent,
    renderLoading: renderLoading,
    renderError: renderError
  })));
};

export default TransactionConfirmModal;